<script lang="ts">
 /**
 * Floating pill showing captured photo thumbnails during multi-capture.
 *
 * @remarks
 * Visible in the `capturing` state, overlaid on the viewfinder above
 * the shutter controls. Shows the last 4 photos as overlapping circles
 * plus a count badge. Frosted glass style (`bg-black/50 backdrop-blur-sm`).
 *
 * **Interactions:**
 * - Tap the photo pill — emits `review` to open the full photo strip drawer
 * - Tap the "Analyze" button — emits `analyze` to skip review and go straight to processing
 */
</script>

<script setup lang="ts">
defineProps<{
  /** Array of photo data URLs. Last 4 are shown as overlapping circle thumbnails. */
  photos: string[]
}>()

defineEmits<{
  /** Fired when the user taps the photo pill. Opens the full review drawer. */
  review: []
  /** Fired when the user taps "Analyze". Skips review, goes straight to processing. */
  analyze: []
  /** Fired when the user taps the X. Clears all photos and resets. */
  clear: []
}>()
</script>

<template>
  <div class="flex items-center gap-2">
    <!-- Photo count pill — tap to review -->
    <button
      class="flex items-center gap-2 rounded-full bg-black/50 py-1 pr-4 pl-1 backdrop-blur-sm"
      @click="$emit('review')"
    >
      <div class="flex -space-x-2">
        <div
          v-for="(photo, i) in photos.slice(-4)"
          :key="i"
          class="h-8 w-8 overflow-hidden rounded-full border-2 border-white/20"
        >
          <img v-if="photo" :src="photo" class="h-full w-full object-cover" alt="" />
          <div v-else class="bg-accented h-full w-full" />
        </div>
      </div>
      <span class="text-xs font-medium text-white">{{ photos.length }}</span>
    </button>

    <!-- Analyze — go straight to processing -->
    <button
      class="rounded-full bg-white px-4 py-2 text-xs font-semibold text-neutral-900 shadow-lg active:scale-95"
      @click="$emit('analyze')"
    >
      Analyze
    </button>

    <!-- Clear photos -->
    <button
      aria-label="Clear photos"
      class="flex size-7 items-center justify-center rounded-full bg-black/50 backdrop-blur-sm active:scale-95"
      @click="$emit('clear')"
    >
      <UIcon name="i-lucide-x" class="size-3.5 text-white/70" />
    </button>
  </div>
</template>
